import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, CircularProgress, Typography,
} from '@material-ui/core';
import { api } from '../../service';

/**
 * Plan v4.7: BO admin manually accepts a wash for a specific washer.
 * Lists active washers (online + free + matching service), lets admin pick one,
 * POSTs /backoffice/order/:id/assign.
 */
const AssignWasherDialog = ({ orderId, serviceId, lat, long, onClose, onAssigned }) => {
  const [washers, setWashers] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get('/backoffice/washer');
        const list = (data && data.data) || [];
        // Filter: active + has the right service ideally; for v1 show all active washers
        const filtered = list.filter((w) => w.status === 'active');
        setWashers(filtered);
      } catch (e) {
        setError(e?.response?.data?.codeDetail || 'failed_to_load_washers');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleAssign = async () => {
    if (!selected) { setError('Pick a washer'); return; }
    setSubmitting(true);
    setError('');
    try {
      await api.post(`/backoffice/order/${orderId}/assign`, { washer_id: selected });
      if (typeof onAssigned === 'function') onAssigned();
    } catch (e) {
      const code = e?.response?.data?.codeDetail || 'assign_failed';
      const status = e?.response?.status;
      setError(`${status || ''} ${code}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign wash to washer</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="textSecondary">
          Order: {orderId}
        </Typography>
        {loading && <CircularProgress />}
        {!loading && (
          <TextField
            select fullWidth label="Washer"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            style={{ marginTop: 12 }}
          >
            {washers.map((w) => (
              <MenuItem key={w.id} value={w.id}>
                {w.firstname} {w.lastname} — {w.work_mode}/{w.current_status} ({w.email})
              </MenuItem>
            ))}
          </TextField>
        )}
        {error && (
          <Typography color="error" style={{ marginTop: 12 }}>⚠ {error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          color="primary" variant="contained"
          onClick={handleAssign}
          disabled={submitting || !selected}
        >
          {submitting ? 'Assigning…' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignWasherDialog;
